import { useMemo, useState } from 'react';
import { Layers, ChevronDown, ChevronRight, Image } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

function QualityBadges({ qc }) {
  if (!qc) return null;
  const entries = Object.entries(qc).filter(([, v]) => v === true);
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {entries.map(([k]) => (
        <span
          key={k}
          className="text-[9px] px-1 py-0.5 rounded border border-sky-500/25 text-sky-300/80"
        >
          {k.replace(/_/g, ' ')}
        </span>
      ))}
    </div>
  );
}

function ComponentLayoutPreview({ components }) {
  if (!components?.length) return null;
  return (
    <div className="space-y-1 text-xs">
      <p className="text-slate-500">Components ({components.length})</p>
      <ul className="space-y-0.5">
        {components.map((c) => (
          <li key={c.component_id} className="text-slate-300 font-mono text-[10px]">
            {c.component_id}
            {' '}
            <span className="text-slate-500">
              @ ({c.x},{c.y}) {c.width}×{c.height} · {c.layer}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ArpCard({ arp, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const layers = arp.layers || [];
  const assets = arp.render_assets || {};

  return (
    <div className="rounded-lg border border-sky-500/20 bg-sky-950/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-sky-950/20"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-sky-100">
            <span className="font-mono text-[11px] text-sky-400/90">{arp.artifact_id}</span>
            {' · '}
            {arp.asset_type}
          </p>
          <p className="text-[10px] text-slate-600 mt-0.5">
            {arp.slide_id} · {arp.concept}
          </p>
        </div>
      </button>
      {open ? (
        <div className="px-3 pb-3 space-y-3 border-t border-sky-900/40 pt-3 text-xs">
          {layers.length ? (
            <div>
              <p className="text-slate-500 mb-1">Layers</p>
              <div className="flex flex-wrap gap-1">
                {layers.map((l) => (
                  <span key={l} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
                    {l}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          <ComponentLayoutPreview components={arp.components} />
          {arp.highlight_targets?.length ? (
            <div>
              <p className="text-slate-500 mb-1">Highlight targets</p>
              <div className="flex flex-wrap gap-1">
                {arp.highlight_targets.map((h) => (
                  <span key={h} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-violet-900/40 text-violet-200">
                    {h}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {arp.animation_targets?.length ? (
            <div>
              <p className="text-slate-500 mb-1">Animation targets</p>
              <div className="flex flex-wrap gap-1">
                {arp.animation_targets.map((a) => (
                  <span key={a} className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/25 text-amber-200/80">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {arp.layout_metadata?.preferred_role ? (
            <p className="text-slate-500">
              Layout role: <span className="text-slate-300">{arp.layout_metadata.preferred_role}</span>
              {arp.layout_metadata.preferred_aspect_ratio ? (
                <span className="text-slate-600">
                  {' '}
                  · {arp.layout_metadata.preferred_aspect_ratio}
                </span>
              ) : null}
            </p>
          ) : null}
          {assets.master_asset ? (
            <p className="text-slate-500">
              Master asset: <span className="text-slate-300">{assets.master_asset.format}</span>
            </p>
          ) : null}
          {arp.accessibility_metadata?.alt_text ? (
            <p className="text-slate-400 line-clamp-2">{arp.accessibility_metadata.alt_text}</p>
          ) : null}
          <QualityBadges qc={arp.rendering_quality_check} />
        </div>
      ) : null}
    </div>
  );
}

export function ArtifactRenderingV3Panel({ data }) {
  const arps = data?.rendered_artifact_objects || [];
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const types = useMemo(() => {
    const set = new Set(arps.map((r) => r.asset_type).filter(Boolean));
    return Array.from(set).sort();
  }, [arps]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return arps.filter((r) => {
      if (typeFilter && r.asset_type !== typeFilter) return false;
      if (!q) return true;
      return (
        r.concept?.toLowerCase().includes(q)
        || r.artifact_id?.toLowerCase().includes(q)
        || r.slide_id?.toLowerCase().includes(q)
        || r.asset_type?.toLowerCase().includes(q)
      );
    });
  }, [arps, filter, typeFilter]);

  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-sky-500/25 bg-sky-950/15 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-sky-200">
          <Layers className="w-4 h-4" />
          {data.total_arps ?? data.total_raos} Artifact Rendering Plans
          {data.step === '5B' ? (
            <span className="text-[10px] text-sky-400/70">(Step 5B · ARP)</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Image className="w-4 h-4" />
          SVG · layout · animation · accessibility metadata
        </div>
        {data.source ? (
          <span className="text-[10px] text-slate-500 ml-auto">Source: {data.source}</span>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {types.length > 1 ? (
          <Select value={typeFilter || '__all__'} onValueChange={(v) => setTypeFilter(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-8 w-[180px] text-xs border-slate-700 bg-slate-900 text-slate-300">
              <SelectValue placeholder="All asset types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All asset types</SelectItem>
              {types.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        {arps.length > 4 ? (
          <input
            type="search"
            placeholder="Filter ARPs…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 min-w-[140px] text-sm rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-slate-200"
          />
        ) : null}
      </div>

      <div className="space-y-2 max-h-[65vh] overflow-y-auto">
        {filtered.map((arp, i) => (
          <ArpCard key={arp.artifact_id} arp={arp} defaultOpen={i < 2} />
        ))}
      </div>
    </div>
  );
}
